import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../hooks/useAuth";

interface NotificationRow {
  id: string;
  notification_key: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  deep_link: string | null;
  read_at: string | null;
  dismissed_at: string | null;
  created_at: string;
}

function timeAgo(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manna_notifications_view")
        .select("*")
        .is("dismissed_at", null)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as unknown as NotificationRow[];
    },
    refetchInterval: 30_000,
  });

  // Supabase Realtime subscription for live pushes
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "platform",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["notifications"] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, qc]);

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("manna_mark_notification_read", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("manna_mark_all_notifications_read");
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("manna_dismiss_notification", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const dismissAll = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("manna_dismiss_all_notifications");
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unreadCount = (data ?? []).filter((n) => !n.read_at).length;

  const handleRowClick = (n: NotificationRow) => {
    if (!n.read_at) markRead.mutate(n.id);
    setAnchor(null);
    if (n.deep_link) navigate(n.deep_link);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <Stack direction="row" alignItems="center" px={2} py={1}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Notifications
          </Typography>
          <Button
            size="small"
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all read
          </Button>
          <Button
            size="small"
            color="inherit"
            disabled={(data ?? []).length === 0 || dismissAll.isPending}
            onClick={() => dismissAll.mutate()}
          >
            Dismiss all
          </Button>
        </Stack>
        <Divider />

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={20} />
          </Box>
        ) : (data ?? []).length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No notifications.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {data!.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleRowClick(n)}
                sx={{
                  alignItems: "flex-start",
                  bgcolor: n.read_at ? "transparent" : "action.hover",
                  borderLeft: n.read_at ? "3px solid transparent" : "3px solid",
                  borderLeftColor: n.read_at ? "transparent" : "primary.main",
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography
                        variant="body2"
                        fontWeight={n.read_at ? 400 : 600}
                        sx={{ flex: 1 }}
                      >
                        {n.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(n.created_at)}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    n.body ? (
                      <Typography variant="caption" color="text.secondary">
                        {n.body}
                      </Typography>
                    ) : null
                  }
                />
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    dismiss.mutate(n.id);
                  }}
                  sx={{ minWidth: 0, alignSelf: "center" }}
                >
                  ×
                </Button>
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
